import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import * as styles from './TopicTreeCardItemDraggable.module.scss';
import CheckRadioControl from '../../components/formInternals/CheckRadioControl';
import SVGIcon from '../../components/SVGIcon';
import dragIcon from '../../icons/non-prefilled/16x16/drag.svg';
import Link from '../../utils/router/Link';
import { DragTypes } from '../constants';
import type { DragObject } from '../utils/types';

interface BaseTopicTreeCardItemDraggableProps {
  to: string;
  type: string;
  children: React.ReactNode;
  dragItem: DragObject;
  onCheckboxChange: () => void;
  reorganizable: boolean;
  isChecked: boolean;
  isCheckboxDisabled: boolean;
  isReorganizing: boolean;
  isMovedOut: boolean;
}

const BaseTopicTreeCardItemDraggable = ({
  to,
  type,
  children,
  dragItem,
  onCheckboxChange,
  reorganizable,
  isChecked,
  isCheckboxDisabled,
  isReorganizing,
  isMovedOut,
}: BaseTopicTreeCardItemDraggableProps) => {
  const canDrag = isReorganizing && !isMovedOut && reorganizable;
  const dragSpec = useMemo(
    () => ({
      type: dragItem.type,
      item: dragItem,
      canDrag,
      monitor: () => null,
    }),
    [canDrag, dragItem],
  );
  const [, dragRef, preview] = useDrag(dragSpec);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);
  const Component = isReorganizing ? 'label' : 'div';
  const isDisabled = isMovedOut || isCheckboxDisabled || !reorganizable;

  return (
    <Component
      className={classNames(styles.container, styles[type], {
        [styles.movedOut]: isMovedOut,
        [styles.draggable]: canDrag,
      })}
      ref={dragRef}
    >
      <div className={styles.dragIconAndTitle}>
        <div className={styles.dragIconContainer}>
          <SVGIcon className={styles.dragIcon} size={10}>
            {dragIcon}
          </SVGIcon>
        </div>
        {isReorganizing && (
          <div className={styles.checkboxContainer}>
            <CheckRadioControl
              type="checkbox"
              disabled={isDisabled}
              onChange={onCheckboxChange}
              checked={!isMovedOut && isChecked}
            />
          </div>
        )}
        <Link
          className={styles.title}
          to={to}
          onClick={(e) => {
            if (!isReorganizing || e.metaKey) return;
            e.preventDefault();
            if (!isDisabled) {
              onCheckboxChange();
            }
          }}
          {...(type === 'topic'
            ? {
                rel: 'noreferrer',
                target: '_blank',
              }
            : {})}
        >
          {children}
        </Link>
      </div>
    </Component>
  );
};

interface TopicTreeCardItemDraggableProps {
  to: string;
  children: React.ReactNode;
  onCheckboxChange: () => void;
  reorganizable: boolean;
  isChecked: boolean;
  isCheckboxDisabled: boolean;
  isReorganizing: boolean;
  isMovedOut: boolean;
  toMove: DragObject['toMove'];
  type: 'post' | 'topic';
}

const TopicTreeCardItemDraggable = ({
  to,
  children,
  onCheckboxChange,
  reorganizable,
  isChecked,
  isCheckboxDisabled,
  isReorganizing,
  isMovedOut,
  toMove,
  type,
}: TopicTreeCardItemDraggableProps) => {
  const dragItem: DragObject = {
    toMove,
    type: DragTypes.mixed,
  };
  if (
    toMove.topicIds.length > 0 &&
    toMove.postKeys.length === 0 &&
    toMove.pinnedPostKeys.length === 0
  ) {
    dragItem.type = DragTypes.topics;
  } else if (
    toMove.topicIds.length === 0 &&
    toMove.postKeys.length > 0 &&
    toMove.pinnedPostKeys.length === 0
  ) {
    dragItem.type = DragTypes.posts;
  } else if (
    toMove.topicIds.length === 0 &&
    toMove.postKeys.length === 0 &&
    toMove.pinnedPostKeys.length > 0
  ) {
    dragItem.type = DragTypes.pinnedPosts;
  } else {
    dragItem.type = DragTypes.mixed;
  }

  return (
    <BaseTopicTreeCardItemDraggable
      to={to}
      type={type}
      dragItem={dragItem}
      onCheckboxChange={onCheckboxChange}
      reorganizable={reorganizable}
      isChecked={isChecked}
      isCheckboxDisabled={isCheckboxDisabled}
      isReorganizing={isReorganizing}
      isMovedOut={isMovedOut}
    >
      {children}
    </BaseTopicTreeCardItemDraggable>
  );
};

export default TopicTreeCardItemDraggable;
